<script lang="ts" setup generic="T extends Nullable<string | number> | undefined">

  import type { InputTypeHTMLAttribute } from 'vue'

  import type { Nullable } from '~/core/types'

  type Props = {
    type?: InputTypeHTMLAttribute
    disabled?: boolean
    resetable?: boolean
    readonly?: boolean
    required?: boolean
    autofocus?: boolean
    light?: boolean
    placeholder?: string
    label?: string
    name: string
    rules?: string
    icon?: string
    errors?: string[]
  }

  const props = withDefaults(defineProps<Props>(), {
    type: 'text',
    disabled: false,
    resetable: false,
    readonly: false,
    required: false,
    autofocus: false,
  })

  const emit = defineEmits<{
    (e: 'reset' | 'blur'): void
  }>()
  defineExpose({
    input,
  })

  const model = defineModel<T>({ required: true, default: null })

  const inputEl = useTemplateRef('inputRef')

  function input($event: Event): void {
    model.value = ($event.target as HTMLInputElement).value as T
  }

  function reset(): void {
    focusInput()
    model.value = '' as T
    emit('reset')
  }

  function focusInput() {
    if (inputEl.value) {
      inputEl.value.focus()
    }
  }

  function blur() {
    emit('blur')
  }

  onMounted(() => {
    if (props.autofocus) {
      // @TODO: Trouver mieux ...
      setTimeout(() => {
        focusInput()
      }, 100)
    }
  })

</script>

<template>
  <div
    class="c-input"
    :class="{'light':props.light}"
  >
    <label
      v-show="label"
      :for="name"
    >
      {{ label }}
    </label>
    <input
      v-if="type !== 'textarea'"
      :id="name"
      ref="inputRef"
      :autofocus="autofocus"
      autocomplete="off"
      :type="type"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :required="required"
      :name="name"
      :value="model"
      :class="{ 'has-icon': icon != null }"
      @input="input"
      @blur="blur"
    >
    <textarea
      v-else
      :id="name"
      :autofocus="autofocus"
      autocomplete="off"
      :type="type"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :required="required"
      :name="name"
      :value="model"
      :class="{ 'has-icon': icon != null }"
      rows="6"
      @input="input"
      @blur="blur"
    />
    <SvgIcon
      v-if="icon"
      :icon="icon"
      :height="20"
      :width="20"
      :desktop-height="20"
      :desktop-width="20"
    />
    <button
      v-if="resetable"
      class="reset"
      @click="reset"
    >
      <SvgIcon
        icon="close"
        :height="12"
        :width="12"
        :desktop-height="12"
        :desktop-width="12"
      />
    </button>
    <InputErrors
      v-if="props.errors && props.errors.length"
      :errors="props.errors"
    />
  </div>
</template>

<style scoped>

.c-input {
  position: relative;
  display: flex;
  flex-direction: column;

  label {
    margin-bottom: 0.5rem;
    color :var(--color-secondary);
    font-family: Open-sans, sans-serif;
    font-size: 0.875em;
    opacity: 0.6;

    &::first-letter {
      text-transform: uppercase;
    }
  }

  input,
  textarea {
    padding: 0.625rem;
    border: none;
    border-radius: 0.25rem;
    background-color: var(--color-secondary-light);
    color: var(--color-secondary);
    font-family: Open-Sans, sans-serif;
    font-size: 1.125em;
    font-weight: 600;
    line-height: 1em;
    opacity: 1;
    outline: none;
    transition: all ease-out 0.15s;

    &[readonly] {
      padding: 0.625em 0;
      background-color: white;
    }

    &[disabled] {
      cursor: not-allowed;
      opacity: 0.5;
    }

    &.has-icon {
      text-indent: 2.5em;
    }

    &::placeholder {
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: 1em;
      font-weight: 600;
      opacity: 0.7;
    }
  }

  &.light {
    input, textarea {
      background-color: white;
    }
  }

  > .c-icon {
    position: absolute;
    bottom: 0.75em;
    left: 1.25em;
    color: var(--color-primary);
  }

  .error {
    display: flex;
    margin-top: 0.25em;
    color: var(--color-error);
    font-family: Open-Sans, sans-serif;
    font-size: 0.75em;
    font-weight: normal;
  }

  button.reset {
    position: absolute;
    top: 0.5em;
    right: 0.5em;
    display: flex;
    padding: 0.5em;
    border-radius: 0.25rem;
    background-color: var(--color-secondary-light);

    .c-icon {
      color: var(--color-secondary);
      opacity: 0.4;
    }
  }
}
</style>
